import React from 'react';
import contentData from '../../data/content.json';

/**
 * Hero Section Component
 * 
 * Full-screen hero section displaying restaurant branding, story, and key information.
 * Features a background image with dark overlay, logo, owner information, history,
 * philosophy, address, and cuisine type.
 * 
 * Requirements:
 * - 1.1: Display restaurant logo
 * - 1.2: Display background image
 * - 1.3: Display information about Christian Fey as owner
 * - 1.4: Display restaurant's history including transition from Ciao Bella
 * - 1.5: Display restaurant's philosophy about welcoming guests
 * - 1.6: Display address "Tibarg, Zum Markt 16, 22459 Hamburg"
 * - 1.7: Highlight Mediterranean-inspired cuisine
 * - 10.1: Mobile-first responsive design
 * - 10.2: Tablet optimization
 * - 10.3: Desktop optimization
 */
export const Hero: React.FC = () => {
  const { hero } = contentData;

  return (
    <section
      id="home"
      className="relative min-h-screen flex items-center justify-center overflow-hidden"
      aria-label="Restaurant introduction"
    >
      {/* Background Image with Dark Overlay */}
      <div className="absolute inset-0 z-0">
        <img
          src="/fey-background.png"
          alt=""
          className="w-full h-full object-cover"
          width={1920}
          height={1080}
          decoding="async"
        />
        {/* Dark overlay for better text readability */}
        <div className="absolute inset-0 bg-black/60" />
      </div>

      {/* Content Container */}
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-20">
        {/* Mobile: Stacked Layout, Tablet/Desktop: Two-Column Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center">
          
          {/* Left Column: Logo and Owner Name */}
          <div className="flex flex-col items-center lg:items-start space-y-6">
            {/* Restaurant Logo */}
            <div className="w-full max-w-[320px] sm:max-w-[420px] lg:max-w-[560px] mx-auto lg:mx-0">
              <img
                src="/LOGO-3D-Transparent.png"
                alt="Restaurant Fey Logo"
                className="w-full h-auto object-contain drop-shadow-2xl"
                width={720}
                height={720}
                decoding="async"
              />
            </div>

            {/* Tagline removed — logo already shows "am Tibarg" */}
          </div>

          {/* Right Column: Story Content */}
          <div className="space-y-6 text-white">
            {/* Restaurant History */}
            <div className="bg-black/40 backdrop-blur-sm p-6 rounded-lg">
              <h2 className="text-2xl sm:text-3xl font-semibold mb-4 text-[#c9a84c]">
                Unsere Geschichte
              </h2>
              <p className="text-base sm:text-lg leading-relaxed text-gray-100">
                {hero.restaurantHistory}
              </p>
            </div>

            {/* Philosophy */}
            <div className="bg-black/40 backdrop-blur-sm p-6 rounded-lg">
              <h2 className="text-2xl sm:text-3xl font-semibold mb-4 text-[#c9a84c]">
                Unsere Philosophie
              </h2>
              <p className="text-base sm:text-lg leading-relaxed text-gray-100">
                {hero.philosophy}
              </p>
            </div>

            {/* Address */}
            <div className="bg-black/40 backdrop-blur-sm p-6 rounded-lg">
              <h2 className="text-xl sm:text-2xl font-semibold mb-3 text-[#c9a84c]">
                Besuchen Sie uns
              </h2>
              <address className="not-italic text-base sm:text-lg text-gray-100">
                {hero.address}
              </address>
            </div>
          </div>
        </div>
      </div>

      {/* Scroll Indicator (Optional Enhancement) */}
      <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 z-10 animate-bounce">
        <svg
          className="w-6 h-6 text-white opacity-75"
          fill="none"
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth="2"
          viewBox="0 0 24 24"
          stroke="currentColor"
          aria-hidden="true"
        >
          <path d="M19 14l-7 7m0 0l-7-7m7 7V3" />
        </svg>
      </div>
    </section>
  );
};
